import React, { ReactElement, useMemo } from 'react';
import { usePage } from '@inertiajs/react';
import { Box, Link, Stack, Typography, useTheme } from '@mui/material';

import { Activity, ActivityEntity } from '@/types';
import {
  formatActivityTimestamp,
  getActivityLinkTarget,
  getActivityNotificationType,
  getActivitySubtitle,
} from '@/pages/activity/utils';
import { MixpanelEventName, MixpanelUtils } from '@/mixpanel';
import { AlphaToHexOpacity } from '@/theme';
import { ActivityItemIcon } from '@/pages/activity/components/ActivityItemIcon';
import { firmRoute } from '@/core/utils';

type Props = {
  activity: Activity;
  isHighlighted: boolean;
};

export const ActivityItem = ({
  activity,
  isHighlighted,
}: Props): ReactElement => {
  const theme = useTheme();
  // Shared globally by HandleInertiaRequests, but null when the user has no current
  // firm — and Activity carries a BelongsToFirm global scope, so every row in the bell
  // belongs to this same firm.
  const {
    props: { firm },
  } = usePage<{ firm?: { isAdvisoryFirm?: boolean } }>();

  const subtitle = useMemo(
    (): string => getActivitySubtitle(activity),
    [activity],
  );

  const linkTarget = getActivityLinkTarget(
    activity,
    Boolean(firm?.isAdvisoryFirm),
  );
  const href = linkTarget
    ? firmRoute(linkTarget.routeName, linkTarget.params)
    : undefined;

  const handleClick = (): void => {
    const notificationType = getActivityNotificationType(activity);

    MixpanelUtils.track(MixpanelEventName.portalAdvisorNotificationClick, {
      ...(activity.entity === ActivityEntity.engagement && {
        notificationType,
        engagementType: activity.entityType,
      }),
      ...(activity.entity === ActivityEntity.taxpayer && {
        notificationType,
        taxpayerType: activity.entityType,
      }),
      ...(activity.entity === ActivityEntity.workflow && {
        notificationType,
      }),
    });
  };

  const activityContent = (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        sx={{
          maxWidth: { xs: 250, md: 440 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            width: 24,
            height: 24,
            marginRight: 1,
            marginTop: 0.5,
          }}
        >
          <ActivityItemIcon entity={activity.entity} type={activity.type} />
        </Box>
        <Stack direction="column">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mr: 1,
            }}
          >
            {activity.name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontSize: 12 }}
          >
            {subtitle}
          </Typography>
        </Stack>
      </Stack>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ pb: 1, fontSize: 12 }}
      >
        {formatActivityTimestamp(new Date(activity.createdAt))}
      </Typography>
    </Stack>
  );

  return (
    <Stack
      direction="column"
      sx={{
        px: 1,
        py: 1,
        borderRadius: isHighlighted ? 0 : 1,
        backgroundColor: isHighlighted
          ? `${theme.palette.primary.main}${AlphaToHexOpacity['level0.08']}`
          : theme.palette.common.white,
        // Only linkable rows get the hover affordance — a row that degraded to a Box
        // would otherwise highlight without doing anything on click.
        ...(href !== undefined && {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }),
      }}
    >
      {href ? (
        <Link
          underline="none"
          color={theme.palette.text.primary}
          href={href}
          onClick={handleClick}
        >
          {activityContent}
        </Link>
      ) : (
        <Box sx={{ color: theme.palette.text.primary }}>{activityContent}</Box>
      )}
    </Stack>
  );
};
